import React, { useEffect, useRef } from 'react';

// Time Traveler card. The helper emits a single kind, climate.time_traveler,
// with a `step` discriminator: "puzzle" / "reveal" / "error". Score + streak
// live in localStorage; daily-mode lockout is a per-UTC-date flag also in
// localStorage. See qlink/time_traveler_endpoints.py for the payload shape.

const LS_SCORE = 'climate.time_traveler.score';
const LS_STREAK = 'climate.time_traveler.streak';
const LS_DAILY_PREFIX = 'climate.time_traveler.daily.';

const MONTH_LABELS = ['J','F','M','A','M','J','J','A','S','O','N','D'];

const CHART_WIDTH = 320;
const TEMP_CHART_HEIGHT = 64;
const PRECIP_CHART_HEIGHT = 48;

// Map temperature (°F) onto a blue→red palette using a fixed reference range
// (0°F deep blue, 100°F deep red). Absolute scale so the same gradient
// works across cities — Phoenix July is unambiguously hot vs Anchorage July.
function tempColor(f) {
  if (f === null || f === undefined) return '#dee2e6';
  const clamped = Math.max(0, Math.min(100, f));
  const t = clamped / 100;
  const r = Math.round(40 + t * (220 - 40));
  const g = Math.round(80 + Math.sin(Math.PI * t) * 60);
  const b = Math.round(220 - t * (220 - 40));
  return `rgb(${r},${g},${b})`;
}

function drawTempChart(canvas, values) {
  if (!canvas || !values) return;
  const ctx = canvas.getContext('2d');
  const dpr = window.devicePixelRatio || 1;
  canvas.width = CHART_WIDTH * dpr;
  canvas.height = TEMP_CHART_HEIGHT * dpr;
  canvas.style.width = `${CHART_WIDTH}px`;
  canvas.style.height = `${TEMP_CHART_HEIGHT}px`;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, CHART_WIDTH, TEMP_CHART_HEIGHT);

  const barWidth = CHART_WIDTH / 12;
  const usableHeight = TEMP_CHART_HEIGHT - 14;
  for (let i = 0; i < 12; i++) {
    const v = values[i];
    const h = v === null || v === undefined
      ? 4
      : Math.max(4, Math.min(usableHeight, (Math.max(0, Math.min(100, v)) / 100) * usableHeight));
    ctx.fillStyle = tempColor(v);
    ctx.fillRect(i * barWidth + 2, TEMP_CHART_HEIGHT - 14 - h, barWidth - 4, h);
    ctx.fillStyle = '#6c757d';
    ctx.font = '10px ui-monospace, monospace';
    ctx.textAlign = 'center';
    ctx.fillText(MONTH_LABELS[i], i * barWidth + barWidth / 2, TEMP_CHART_HEIGHT - 2);
  }
}

function drawPrecipChart(canvas, values) {
  if (!canvas || !values) return;
  const ctx = canvas.getContext('2d');
  const dpr = window.devicePixelRatio || 1;
  canvas.width = CHART_WIDTH * dpr;
  canvas.height = PRECIP_CHART_HEIGHT * dpr;
  canvas.style.width = `${CHART_WIDTH}px`;
  canvas.style.height = `${PRECIP_CHART_HEIGHT}px`;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, CHART_WIDTH, PRECIP_CHART_HEIGHT);

  // Scale relative to the year's own maximum so the shape is visible even
  // for low-precip cities (Phoenix). 12 inches as a floor maximum keeps
  // tiny-precip months from looking gigantic.
  const observed = values.filter(v => v !== null && v !== undefined);
  const max = Math.max(12, ...observed);
  const barWidth = CHART_WIDTH / 12;
  const usableHeight = PRECIP_CHART_HEIGHT - 4;
  for (let i = 0; i < 12; i++) {
    const v = values[i] || 0;
    const h = Math.max(2, (v / max) * usableHeight);
    ctx.fillStyle = '#4c6ef5';
    ctx.fillRect(i * barWidth + 2, PRECIP_CHART_HEIGHT - h, barWidth - 4, h);
  }
}

function fmtStat(value, suffix = '', digits = 0) {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'number') {
    return digits > 0 ? `${value.toFixed(digits)}${suffix}` : `${Math.round(value)}${suffix}`;
  }
  return String(value);
}

function loadInt(key, fallback = 0) {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return fallback;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : fallback;
  } catch (e) {
    return fallback;
  }
}

function saveInt(key, value) {
  try { window.localStorage.setItem(key, String(value)); } catch (e) { /* private mode */ }
}

function utcToday() {
  return new Date().toISOString().slice(0, 10);
}


// ---------- step renderers ------------------------------------------------

function PuzzleStep({ structured, onAction }) {
  const tempRef = useRef(null);
  const precipRef = useRef(null);

  useEffect(() => {
    drawTempChart(tempRef.current, structured.year_card?.months_tmax_f);
  }, [structured.year_card]);
  useEffect(() => {
    drawPrecipChart(precipRef.current, structured.year_card?.months_precip_in);
  }, [structured.year_card]);

  const card = structured.year_card || {};
  const stats = card.stats || {};
  const score = loadInt(LS_SCORE);
  const streak = loadInt(LS_STREAK);

  return (
    <div style={{
      marginBottom: '10px', padding: '14px', background: '#f8f9fa',
      borderRadius: '8px', border: '1px solid #e9ecef',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '10px' }}>
        <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#212529' }}>?</div>
        <div style={{ fontSize: '0.85rem', color: '#6c757d' }}>
          {card.city_name || 'Loading…'}
        </div>
      </div>
      <div style={{ fontSize: '0.7rem', color: '#6c757d', marginBottom: '4px' }}>Monthly avg temperature (°F)</div>
      <canvas ref={tempRef} />
      <div style={{ fontSize: '0.7rem', color: '#6c757d', margin: '8px 0 4px' }}>Monthly precipitation (in)</div>
      <canvas ref={precipRef} />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px', margin: '12px 0', fontSize: '0.8rem' }}>
        <span>Hottest day: <strong>{fmtStat(stats.hottest_day_f, '°F')}</strong></span>
        <span>Coldest day: <strong>{fmtStat(stats.coldest_day_f, '°F')}</strong></span>
        <span>Days &gt;90°F: <strong>{fmtStat(stats.days_above_90)}</strong></span>
        <span>Days &lt;32°F: <strong>{fmtStat(stats.days_below_32)}</strong></span>
        <span>Total rain: <strong>{fmtStat(stats.precip_total_in, ' in', 1)}</strong></span>
        <span>Total snow: <strong>{fmtStat(stats.snow_total_in, ' in', 1)}</strong></span>
        <span>Last spring frost: <strong>{stats.last_spring_freeze || '—'}</strong></span>
        <span>First fall frost: <strong>{stats.first_fall_freeze || '—'}</strong></span>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' }}>
        {(structured.options || []).map((opt) => (
          <button
            key={opt.year}
            onClick={() => onAction(opt.action, structured)}
            style={{
              flex: '1 1 0', minWidth: '64px',
              fontSize: '1.1rem', fontWeight: 700,
              padding: '10px 14px', border: '1px solid #4c6ef5',
              borderRadius: '6px', background: 'white', color: '#364fc7',
              cursor: 'pointer',
            }}
          >
            {opt.year}
          </button>
        ))}
      </div>

      <div style={{ marginTop: '8px', fontSize: '0.7rem', color: '#6c757d', textAlign: 'right' }}>
        Score: {score} · Streak: {streak} · Mode: {structured.mode} · Difficulty: {structured.difficulty}
      </div>
    </div>
  );
}

function RevealStep({ structured, onAction }) {
  const correct = !!structured.correct;
  const base = structured.base_points || 0;
  const incomingStreak = loadInt(LS_STREAK);
  const bonus = correct ? 10 * incomingStreak : 0;
  const totalPoints = base + bonus;
  const newStreak = correct ? incomingStreak + 1 : 0;

  // Persist score + streak once per reveal (puzzle_id is the dedupe key).
  const recordedKey = `climate.time_traveler.recorded.${structured.puzzle_id}`;
  useEffect(() => {
    try {
      if (window.localStorage.getItem(recordedKey)) return;
      window.localStorage.setItem(recordedKey, '1');
      const prior = loadInt(LS_SCORE);
      saveInt(LS_SCORE, prior + totalPoints);
      saveInt(LS_STREAK, newStreak);
      // Daily lockout: mark today's UTC date as completed.
      if (structured.next_action?.prefilled?.mode === 'daily' || structured.mode === 'daily') {
        window.localStorage.setItem(LS_DAILY_PREFIX + utcToday(), structured.puzzle_id);
      }
    } catch (e) { /* private mode */ }
  }, [recordedKey, totalPoints, newStreak, structured.mode, structured.next_action]);

  const banner = correct ? '✓ Correct' : '✗ Wrong';
  const bannerBg = correct ? '#d3f9d8' : '#ffe3e3';
  const bannerColor = correct ? '#2b8a3e' : '#c92a2a';

  return (
    <div style={{
      marginBottom: '10px', padding: '14px', background: '#f8f9fa',
      borderRadius: '8px', border: '1px solid #e9ecef',
    }}>
      <div style={{
        padding: '8px 12px', background: bannerBg, color: bannerColor,
        borderRadius: '6px', fontWeight: 700, marginBottom: '12px',
      }}>
        {banner} — that was <strong>{structured.actual_year}</strong>.
        {!correct && <span> You picked {structured.selected_year}.</span>}
      </div>

      <div style={{ fontSize: '0.85rem', marginBottom: '12px' }}>
        +{base} base{bonus > 0 && <span> · +{bonus} streak bonus</span>} = <strong>{totalPoints}</strong> points.
        {correct && <span> Streak now {newStreak}.</span>}
      </div>

      {structured.tells && structured.tells.length > 0 && (
        <div style={{ marginBottom: '12px' }}>
          <div style={{ fontSize: '0.7rem', color: '#6c757d', textTransform: 'uppercase', marginBottom: '4px' }}>
            Tells
          </div>
          {structured.tells.map((t, i) => (
            <div key={i} style={{
              fontSize: '0.8rem', padding: '6px 10px', marginBottom: '4px',
              background: 'white', borderLeft: '3px solid #4c6ef5', borderRadius: '4px',
            }}>
              <strong>{t.label}:</strong> {t.delta > 0 ? '+' : ''}{t.delta} vs {t.decade_name} normal.
            </div>
          ))}
        </div>
      )}

      {structured.next_action && (
        <button
          onClick={() => onAction(structured.next_action, structured)}
          style={{
            padding: '10px 16px', fontSize: '0.95rem', fontWeight: 600,
            border: 'none', borderRadius: '6px', background: '#4c6ef5', color: 'white',
            cursor: 'pointer',
          }}
        >
          Next puzzle →
        </button>
      )}
    </div>
  );
}

function ErrorStep({ structured }) {
  return (
    <div style={{
      marginBottom: '10px', padding: '12px 14px',
      background: '#fff5f5', color: '#c92a2a',
      borderRadius: '8px', border: '1px solid #ffc9c9',
      fontSize: '0.85rem',
    }}>
      Time Traveler: {structured.error || 'unknown error'}
    </div>
  );
}


// ---------- entry ---------------------------------------------------------

export default function TimeTravelerCard({ structured, onAction }) {
  switch (structured.step) {
    case 'puzzle':
      return <PuzzleStep structured={structured} onAction={onAction} />;
    case 'reveal':
      return <RevealStep structured={structured} onAction={onAction} />;
    case 'error':
    default:
      return <ErrorStep structured={structured} />;
  }
}
